import { AsyncPipe } from "@angular/common";
import {
    Component,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { Observable, Subject, combineLatest } from "rxjs";
import {
    debounceTime,
    distinctUntilChanged,
    map,
    shareReplay,
    takeUntil
} from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { GuidUtils } from "~app/common/utils/guid-utils";
import { canEdit } from "~app/role-permissions";
import { BUSY_CHANNEL_NAME } from "../../constants/virkeskopngx-constants";
import { Atgard } from "../../models/atgard-models";
import { VacsKontraktstatus } from "../../models/kontrakt-models";
import { TradslagDTO } from "../../models/mbf-models";
import { AtgardSkogsvardTradslagPlanta } from "../../models/skogsvard/skogsvard-models";
import { AtgardMediatorService } from "../../services/atgard-mediator.service";
import { KontraktMediatorService } from "../../services/kontrakt-mediator.service";
import { SkogsvardTradslagService } from "../../services/skogsvard-tradslag.service";
import { SkogsvardService } from "../../services/skogsvard.service";
import { SkogsvardTradslagPlantorRowComponent } from "./skogsvard-tradslag-plantor-row/skogsvard-tradslag-plantor-row.component";
@Component({
    selector: "rask-skogsvard-tradslag-plantor",
    templateUrl: "./skogsvard-tradslag-plantor.component.html",
    styleUrls: ["./skogsvard-tradslag-plantor.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        AsyncPipe,
        SkogsvardTradslagPlantorRowComponent,
        ActionButtonComponent
    ]
})
export class SkogsvardTradslagPlantorComponent implements OnInit, OnDestroy {
    allatradslag: TradslagDTO[];
    planttyper: Map<number, string[]>;
    skogsvardTradslagPlantaLista: AtgardSkogsvardTradslagPlanta[] = [];

    protected kontraktReadonly = false;
    protected isVacsAvraknat$: Observable<boolean>;
    protected skogsvardsAtgard: Atgard;
    protected isWorking$: Observable<boolean>;

    private readonly atgardMediatorService = inject(AtgardMediatorService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly ngDestroyed$ = new Subject<void>();
    private readonly skogsvardService = inject(SkogsvardService);
    private readonly skogsvardTradslagService = inject(SkogsvardTradslagService);
    private readonly busyService = inject(BusyService);

    private readonly canEdit = canEdit();

    get kontrakt$() {
        return this.kontraktMediatorService.kontrakt$;
    }

    ngOnDestroy(): void {
        this.ngDestroyed$.next();
        this.ngDestroyed$.complete();
    }

    ngOnInit(): void {
        this.isWorking$ = this.busyService.getChannel(BUSY_CHANNEL_NAME).isWorking.pipe(
            distinctUntilChanged(),
            debounceTime(200),
            shareReplay(1)
        );

        this.atgardMediatorService.atgard$
            .subscribe(atgard => this.skogsvardsAtgard = atgard);

        this.kontraktMediatorService.isReadonly$
            .pipe(
                map(readonly => readonly || !this.canEdit()),
                takeUntil(this.ngDestroyed$))
            .subscribe(readonly => this.kontraktReadonly = readonly);

        this.isVacsAvraknat$ = this.kontraktMediatorService.kontrakt$.pipe(
            map(kontrakt => kontrakt?.vacsKontraktstatus === VacsKontraktstatus.Avraknat)
        );

        this.init();
    }

    protected addRow() {
        if (this.kontraktReadonly) {
            return;
        }
        const model = {
            guid: GuidUtils.newGuid(),
            tradslagId: null,
            planttyp: null,
            plantantal: null,
            arealandel: 100,
            malPlantorHA: null,
            plantskydd: null
        } as AtgardSkogsvardTradslagPlanta;

        this.skogsvardTradslagService.updateSkogsvardTradslag(model);
    }

    protected deleted(tradslag: AtgardSkogsvardTradslagPlanta) {
        this.skogsvardTradslagService.deleteSkogsvardTradslag(tradslag.guid);
    }

    private init(): void {
        const getSkogsvardPlanttyper$ = this.skogsvardService.getSkogsvardPlanttyper();
        const getTradslag$ = this.skogsvardService.getTradslag();
        const getskogsvardTradslag$ = this.skogsvardTradslagService.skogsvardTradslag$;

        getskogsvardTradslag$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe(plantor => {
                this.skogsvardTradslagPlantaLista = plantor ?? [];
            });

        combineLatest([
            getSkogsvardPlanttyper$,
            getTradslag$
        ])
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe(([
                typer,
                tradslag
            ]) => {
                this.planttyper = typer;
                this.allatradslag = tradslag ?? [];
            });
    }
}
